import type { CaseManifestV1 } from "@cold-case/shared/case-manifest";

import { isPermQuestionId } from "./perm-question-id.js";
import type { CoherenceJournal } from "./dialog-parser.js";

/** Journal: userId → NPC instance_ids that granted building search clearance (dialog progress). */
export type BuildingClearanceByUser = Record<string, string[]>;

export function userBuildingClearanceSet(
  journal: CoherenceJournal,
  userId: string | undefined
): Set<string> {
  const uid = userId?.trim();
  if (!uid) return new Set();
  const row = journal.building_clearance_by_user?.[uid];
  return new Set((row ?? []).map(String));
}

/**
 * True when this player may search a building gated by `approverNpcId`.
 * Co-op: only `building_clearance_by_user` (dialog progress). Solo: same, plus legacy `talked_to_npcs`.
 */
function npcHasPermGrantQuestions(manifest: CaseManifestV1 | undefined, approverNpcId: string): boolean {
  if (!manifest) return false;
  const npc = manifest.npcs.find((n) => n.instance_id === approverNpcId);
  return (npc?.dialog?.interrogation ?? []).some((q) => isPermQuestionId(q.question_id));
}

export function hasBuildingClearance(
  journal: CoherenceJournal,
  approverNpcId: string,
  opts?: { userId?: string; playerCount?: number; manifest?: CaseManifestV1 }
): boolean {
  const perm = approverNpcId.trim();
  if (!perm) return true;

  const uid = opts?.userId?.trim();
  const playerCount = opts?.playerCount ?? 1;
  if (uid && userBuildingClearanceSet(journal, uid).has(perm)) return true;

  const legacyTalkedOk =
    playerCount <= 1 && !npcHasPermGrantQuestions(opts?.manifest, perm);
  if (legacyTalkedOk) {
    const talked = new Set([...(journal.talked_to_npcs ?? [])].map(String));
    return talked.has(perm);
  }

  return false;
}

export function buildingClearanceJournalPatch(
  userId: string,
  approverNpcId: string
): { building_clearance_by_user: BuildingClearanceByUser } {
  const uid = userId.trim();
  const npc = approverNpcId.trim();
  return { building_clearance_by_user: { [uid]: [npc] } };
}
